import { Controller, get, useFormContext, ControllerRenderProps, FieldValues } from "react-hook-form";

import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import { useReviewFieldErrorClear } from "@RequestFormV2/hooks/useReviewFieldErrorClear";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import BaseSelect from "./BaseSelect";
import type { FormSelectProps } from "./types";

export default function FormSelect({
  name = "",
  label = "",
  required = false,
  defaultValue,
  errorMessage,
  hasError = false,
  hideError = false,
  hintText = "",
  width = "100%",
  shouldUnregister = false,
  saveOnChange = true,
  coerceDisplayValue,
  parseFieldValue,
  value,
  onChange,
  registerOptions,
  ...baseSelectProps
}: FormSelectProps) {
  const formContext = useFormContext();
  const control = formContext ? formContext.control : null;
  const errors = formContext?.formState?.errors;
  const saveContext = useRequestFormSaveOrNull();
  const clearReviewError = useReviewFieldErrorClear(name || undefined);

  const commitField = (field: ControllerRenderProps<FieldValues, string>) => {
    field.onBlur();
  };

  const renderSelect = (
    fieldValue: unknown,
    field?: ControllerRenderProps<FieldValues, string>,
    error: { message?: string } | null = null,
  ) => {
    const displayValue = coerceDisplayValue
      ? coerceDisplayValue(fieldValue)
      : ((fieldValue as string | null) ?? null);

    const handleChange = (val: string | null) => {
      const parsedValue = parseFieldValue ? parseFieldValue(val) : val;
      if (field) {
        field.onChange(parsedValue);
        commitField(field);
      }
      onChange?.(val);
      clearReviewError(parsedValue);
      if (saveOnChange && name && saveContext) {
        saveContext.saveField(name, parsedValue).catch(() => undefined);
      }
    };

    return (
      <BaseSelect
        {...baseSelectProps}
        name={name || baseSelectProps.name}
        label={label}
        required={required}
        hintText={hintText}
        error={error}
        hasError={hasError}
        width={width}
        value={displayValue}
        onChange={handleChange}
        onBlur={field?.onBlur}
      />
    );
  };

  if (name && control) {
    return (
      <>
        <ReviewFieldErrorClearWatcher fieldPath={name} />
        <Controller
          name={name}
          control={control}
          shouldUnregister={shouldUnregister}
          rules={{ required, ...registerOptions }}
          defaultValue={defaultValue ?? undefined}
          render={({ field, fieldState }) => {
            const fieldError = fieldState.error ?? (name ? get(errors, name) : undefined);
            const fallbackError = errorMessage ? { message: errorMessage } : null;
            const error = hideError ? null : (fieldError ?? fallbackError);
            return renderSelect(field.value, field, error);
          }}
        />
      </>
    );
  }

  return renderSelect(value ?? null, undefined, errorMessage ? { message: errorMessage } : null);
}
